// First-run setup for self-signup orgs. Blocks the app (a dialog with no
// close) until the org's SMS DLT setup is in place:
//   - "I don't have DLT yet"  → register with SmartPing or contact support
//   - "I already have DLT"    → PE ID and headers, live at once (templates are
//     added later from Templates/Headers)
// Rules live in lib/firstRun; the backend decides when it applies
// (GET /api/v1/me/onboarding → showFirstRun).
import React, {useEffect, useState} from 'react';
import {
  Alert, Box, Button, Card, CardActionArea, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Link, Stack, TextField, Typography
} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {amber} from '@mui/material/colors';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import InfoIcon from '@mui/icons-material/Info';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import useAuthUser from '../../auth/useAuthUser';
import {isImpersonating} from '../../lib/impersonation';
import {FIRST_RUN_SUPPORT} from '../../lib/constant';
import {
  CHOICE_DLT_PENDING, errorMessage, firstRunView, initialStep, MAX_SENDER_IDS, normalizePeId,
  parseSenderIds, validateDltForm
} from '../../lib/firstRun';
import {chooseNoDlt, reloadApp, submitExistingDlt} from '../../actions/firstRun';
import useMyOnboarding, {refreshMyOnboarding} from '../../lib/useMyOnboarding';

const FirstRun = () => {
  const {roles, role, logout} = useAuthUser();
  // The shell remounts on every route; the shared response is fetched once
  // per page load (the offer banner reads the same one).
  const shared = useMyOnboarding();
  const [onboarding, setOnboarding] = useState(shared);
  const [step, setStep] = useState(() => initialStep(shared));

  useEffect(() => {
    if (shared) {
      setOnboarding(shared);
      setStep(initialStep(shared));
    }
  }, [shared]);

  const load = () => refreshMyOnboarding()
    .catch(() => setOnboarding(null)); // never block the app on a failed check

  const view = firstRunView(onboarding, {roles, role, impersonating: isImpersonating()});
  if (view === 'none') {
    return null;
  }

  // No DLT and the DLT form are wide (two columns / help text + contacts in a
  // row); the choose step is medium; the short "ask your admin" note is narrow.
  const noDlt = view === 'setup' && step === 'no_dlt';
  const wide = noDlt || (view === 'setup' && step === 'dlt_form');
  const width = wide ? 'lg' : (view === 'setup' ? 'md' : 'sm');
  return (
    <Dialog open fullWidth maxWidth={ width } disableEscapeKeyDown>
      {/* Sign out sits top-right on every step, not in the footers. */}
      <Box sx={{display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1}}>
        <SignOut logout={ logout } />
      </Box>
      {view === 'setup' && step !== 'choose' ? null : <DltNotice />}
      {view === 'wait_admin' ? <WaitForAdmin /> : null}
      {view === 'setup' && step === 'choose' ? <Choose onChoose={ setStep } /> : null}
      {noDlt ? <NoDlt onHaveDlt={ () => setStep('dlt_form') } onBack={ () => setStep('choose') } /> : null}
      {view === 'setup' && step === 'dlt_form' ?
        <ExistingDltForm onBack={ () => setStep('choose') } onDone={ load } /> : null}
    </Dialog>
  );
};

const SignOut = ({logout}) => (
  <Button size="small" onClick={ logout } sx={{color: 'text.secondary', fontSize: 12, textTransform: 'none'}}>
    Sign out
  </Button>
);

// A tinted box with a round badge: amber "!" (warning) or blue "i" (info).
// Explicit colours: the app theme keeps MUI's orange warning / light-blue
// info, the design wants amber and the primary blue.
const CALLOUT_TONES = {
  warning: {badge: amber[600], bg: '#FEF6E4'},
  info: {badge: (theme) => theme.palette.primary.main, bg: (theme) => alpha(theme.palette.primary.main, 0.06)}
};

const Callout = ({tone, sx, children}) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, borderRadius: 1.5,
    bgcolor: CALLOUT_TONES[tone].bg, ...sx
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      width: 24, height: 24, borderRadius: '50%', bgcolor: CALLOUT_TONES[tone].badge, color: 'common.white',
      fontSize: 15, fontWeight: 700, lineHeight: 1
    }}>
      {tone === 'warning' ? <PriorityHighIcon sx={{fontSize: 16}} /> : 'i'}
    </Box>
    <Box sx={{flex: 1, minWidth: 0, fontSize: 13}}>{children}</Box>
  </Box>
);

// One-line amber notice at the top of the first-run steps.
const DltNotice = () => (
  <Callout tone="warning" sx={{mx: 3, mt: 0.5}}>
    <Box component="span" sx={{fontSize: 15}}>
      <Box component="strong" sx={{fontWeight: 600}}>DLT registration is mandatory</Box> as per TRAI guidelines
      for sending business SMS in India.
    </Box>
  </Callout>
);

const WaitForAdmin = () => (
  <>
    <DialogTitle>Your account is almost ready</DialogTitle>
    <DialogContent>
      <Typography>
        Your organization&apos;s admin needs to add its DLT registration details before anyone can send SMS.
        Please ask them to sign in and complete the setup.
      </Typography>
    </DialogContent>
  </>
);

// A full-width option row: round icon, title + description, chevron.
const OptionRow = ({icon, title, body, onClick}) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2,
      transition: 'background-color 150ms, border-color 150ms',
      // Hover / keyboard focus: dim blue background and a primary border.
      '&:hover, &:focus-within': {
        borderColor: 'primary.main',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05)
      }
    }}
  >
    <CardActionArea
      onClick={ onClick }
      sx={{
        display: 'flex', alignItems: 'center', gap: 2.5, px: 2.5, py: 2,
        // The card colours itself; drop CardActionArea's grey overlay.
        '& .MuiCardActionArea-focusHighlight': {display: 'none'}
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        width: 56, height: 56, borderRadius: '50%', color: 'primary.main',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
      }}>
        {icon}
      </Box>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography sx={{fontSize: 18, fontWeight: 600, mb: 0.5}}>{title}</Typography>
        <Typography color="text.secondary" sx={{fontSize: 15.5, lineHeight: 1.5}}>{body}</Typography>
      </Box>
      <ChevronRightIcon color="action" />
    </CardActionArea>
  </Card>
);

const DemoLink = () => {
  const {demoVideoUrl} = FIRST_RUN_SUPPORT;
  if (!demoVideoUrl) {
    return <span />;
  }
  return (
    <Link
      href={ demoVideoUrl }
      target="_blank"
      rel="noopener noreferrer"
      sx={{display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 15}}
    >
      <PlayCircleIcon color="primary" sx={{fontSize: 22}} />
      Watch Combirds SMS Demo
      <OpenInNewIcon sx={{fontSize: 15}} />
    </Link>
  );
};

const Choose = ({onChoose}) => {
  const [error, setError] = useState('');
  const noDlt = () => chooseNoDlt(CHOICE_DLT_PENDING)
    .then(() => {
      onChoose('no_dlt');
      refreshMyOnboarding().catch(() => {}); // later mounts resume at the new choice
    })
    .catch((err) => setError(errorMessage(err)));
  return (
    <>
      <DialogTitle sx={{pt: 2.5, pb: 0.5, fontSize: 28, fontWeight: 700, lineHeight: 1.25}}>
        Set up your SMS in minutes
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{fontSize: 16, mb: 2.5}}>
          To start sending SMS from your Combirds account, let us know your DLT registration status.
        </Typography>
        {error ? <Alert severity="error" sx={{mb: 2}}>{error}</Alert> : null}
        <Stack spacing={ 1.5 }>
          <OptionRow
            icon={ <FactCheckOutlinedIcon sx={{fontSize: 30}} /> }
            title="I have DLT registration"
            body={ 'Enter your Principal Entity (PE) ID and registered headers to proceed with SMS setup. ' +
              'You can add approved templates afterward.' }
            onClick={ () => onChoose('dlt_form') }
          />
          <OptionRow
            icon={ <HeadsetMicOutlinedIcon sx={{fontSize: 30}} /> }
            title="I need help with DLT registration"
            body="Our team will guide you through the registration process and help you get started with Combirds."
            onClick={ noDlt }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{justifyContent: 'flex-start', px: 3, py: 1.5, borderTop: 1, borderColor: 'divider'}}>
        <DemoLink />
      </DialogActions>
    </>
  );
};

// A round tinted icon; colour is a palette path or a hex.
const RoundIcon = ({color = 'primary.main', bg, size = 48, children}) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    width: size, height: size, borderRadius: '50%', color,
    bgcolor: bg || ((theme) => alpha(theme.palette.primary.main, 0.08))
  }}>
    {children}
  </Box>
);

// One contact in the support card: round icon, small label, value link.
// `nowrap` keeps the value on one line and the contact at its natural width.
const Contact = ({icon, iconColor, iconBg, label, href, linkColor, nowrap, children}) => (
  <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flex: nowrap ? '0 0 auto' : 1, minWidth: 0}}>
    <RoundIcon color={ iconColor } bg={ iconBg } size={ 44 }>{icon}</RoundIcon>
    <Box sx={{minWidth: 0}}>
      <Typography color="text.secondary" sx={{fontSize: 12}}>{label}</Typography>
      <Link
        href={ href }
        underline="hover"
        color={ linkColor }
        sx={{fontSize: 16, fontWeight: 600, ...(nowrap ? {whiteSpace: 'nowrap'} : {wordBreak: 'break-all'})}}
      >
        {children}
      </Link>
    </Box>
  </Box>
);

// Support phone | email, side by side (stacked on small screens).
const SupportContacts = ({nowrap, sx}) => {
  const {email, phone} = FIRST_RUN_SUPPORT;
  return (
    <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 2, ...sx}}>
      {phone ?
        <Contact icon={ <PhoneOutlinedIcon /> } label="Phone" href={ `tel:${phone}` } linkColor="text.primary" nowrap={ nowrap }>
          {phone}
        </Contact>
        : null}
      {phone && email ? <Divider orientation="vertical" flexItem sx={{display: {xs: 'none', sm: 'block'}}} /> : null}
      {email ?
        <Contact
          icon={ <MailOutlineIcon /> }
          iconColor="#7C3AED"
          iconBg={ alpha('#7C3AED', 0.1) }
          label="Email"
          href={ `mailto:${email}` }
          nowrap={ nowrap }
        >
          {email}
        </Contact>
        : null}
    </Box>
  );
};

const DLT_DOCUMENTS = [
  ['Business PAN', 'PAN of your registered business.'],
  ['Business registration document', 'e.g. GST certificate, MSME certificate, etc.'],
  ['Letter of Authorization (if applicable)', 'Required for Private Limited companies or when authorized by a director.']
];

const NoDlt = ({onHaveDlt, onBack}) => {
  const {dltRegisterUrl, dltRegisterFee, dltValidity} = FIRST_RUN_SUPPORT;
  const sectionTitle = {fontSize: 15, fontWeight: 600, mb: 0.75};
  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, mx: 3, mt: 0.5, px: 2, py: 1.25, borderRadius: 1.5,
        bgcolor: '#FEF3E6', color: '#B45309', fontSize: 14
      }}>
        <WarningAmberRoundedIcon sx={{fontSize: 22}} />
        Finish setting up SMS for your account to start sending.
      </Box>
      <DialogContent sx={{pt: 2.5}}>
        <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: 3}}>
          <Box sx={{flex: 3, minWidth: 0}}>
            <Typography component="h2" sx={{fontSize: 26, fontWeight: 700, lineHeight: 1.25, mb: 1}}>
              Let&apos;s get your DLT registration done
            </Typography>
            <Typography color="text.secondary" sx={{fontSize: 14, mb: 2.5}}>
              To start sending SMS from your Combirds account, you need to complete DLT registration in India. You
              can register yourself using our recommended partner, or get help from our team.
            </Typography>
            <Stack spacing={ 2 }>
              {dltRegisterUrl ?
                <Box sx={{p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04)}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: {xs: 'wrap', sm: 'nowrap'}}}>
                    <RoundIcon><OpenInNewIcon /></RoundIcon>
                    <Box sx={{flex: 1, minWidth: 180}}>
                      <Typography sx={sectionTitle}>Register on your own (Recommended)</Typography>
                      <Typography color="text.secondary" sx={{fontSize: 13}}>
                        Sign up on SmartPing to complete your DLT registration. SmartPing is a trusted and widely
                        used DLT operator.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      href={ dltRegisterUrl }
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={ <OpenInNewIcon /> }
                      sx={{textTransform: 'none', fontWeight: 600, flexShrink: 0}}
                    >
                      Register on SmartPing
                    </Button>
                  </Box>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, px: 1.5, py: 0.75, borderRadius: 1,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06), fontSize: 12, color: 'text.secondary'
                  }}>
                    <InfoIcon color="primary" sx={{fontSize: 18}} />
                    You can also register through other DLT operators like Airtel, Jio, Vodafone Idea (Vi), BSNL, etc.
                  </Box>
                </Box>
                : null}
              <Card variant="outlined" sx={{p: 2, borderRadius: 2}}>
                <Box sx={{display: 'flex', gap: 2, mb: 2}}>
                  <RoundIcon color="success.main" bg={ (theme) => alpha(theme.palette.success.main, 0.1) }>
                    <HeadsetMicOutlinedIcon />
                  </RoundIcon>
                  <Box>
                    <Typography sx={sectionTitle}>Contact our Support</Typography>
                    <Typography color="text.secondary" sx={{fontSize: 13}}>
                      Need help with DLT registration or have any questions? Our team is here to assist you.
                    </Typography>
                  </Box>
                </Box>
                <SupportContacts nowrap />
              </Card>
            </Stack>
          </Box>
          <Box sx={{
            flex: 2, minWidth: 0, p: 2.5, borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03)
          }}>
            <Typography sx={sectionTitle}>What is DLT registration?</Typography>
            <Typography color="text.secondary" sx={{fontSize: 13}}>
              DLT (Distributed Ledger Technology) registration is mandatory in India for sending commercial SMS. You
              need to register your business and get approved SMS headers and templates to start sending SMS.
            </Typography>
            <Divider sx={{my: 2}} />
            <Typography sx={sectionTitle}>Documents required</Typography>
            <Typography color="text.secondary" sx={{fontSize: 13, mb: 1.5}}>
              To complete DLT registration, you typically need:
            </Typography>
            <Stack spacing={ 1.5 }>
              {DLT_DOCUMENTS.map(([title, body], i) => (
                <Box key={ title } sx={{display: 'flex', gap: 1.5}}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    width: 26, height: 26, borderRadius: '50%', fontSize: 13, fontWeight: 600, color: 'common.white',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.7)
                  }}>
                    {i + 1}
                  </Box>
                  <Box>
                    <Typography sx={{fontSize: 13, fontWeight: 600}}>{title}</Typography>
                    <Typography color="text.secondary" sx={{fontSize: 12}}>{body}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
            {dltRegisterFee || dltValidity ?
              <>
                <Divider sx={{my: 2}} />
                <Typography sx={sectionTitle}>Pricing (via SmartPing)</Typography>
                <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                  {dltRegisterFee ?
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
                      <RoundIcon size={ 40 }><CurrencyRupeeIcon sx={{fontSize: 20}} /></RoundIcon>
                      <Box>
                        <Typography color="text.secondary" sx={{fontSize: 12}}>Registration fee</Typography>
                        <Typography sx={{fontSize: 16, fontWeight: 600}}>{dltRegisterFee}</Typography>
                        <Typography color="text.secondary" sx={{fontSize: 11}}>(payable to SmartPing)</Typography>
                      </Box>
                    </Box>
                    : null}
                  {dltValidity ?
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
                      <RoundIcon size={ 40 }><CalendarMonthOutlinedIcon sx={{fontSize: 20}} /></RoundIcon>
                      <Box>
                        <Typography color="text.secondary" sx={{fontSize: 12}}>Validity</Typography>
                        <Typography sx={{fontSize: 16, fontWeight: 600}}>{dltValidity}</Typography>
                      </Box>
                    </Box>
                    : null}
                </Box>
              </>
              : null}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{justifyContent: 'space-between', px: 3, py: 1.5, borderTop: 1, borderColor: 'divider'}}>
        {/* Back to the two choices (I have DLT / I need help). */}
        <Button onClick={ onBack } sx={{textTransform: 'none', fontWeight: 600, fontSize: 15}}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={ onHaveDlt }
          endIcon={ <ArrowForwardIcon /> }
          sx={{textTransform: 'none', fontWeight: 600, fontSize: 15}}
        >
          I have DLT registration now
        </Button>
      </DialogActions>
    </>
  );
};

// A numbered step: blue number, heading (the field's accessible name), field.
const NumberedStep = ({n, id, title, children}) => (
  <Box sx={{display: 'flex', gap: 2}}>
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: 'common.white',
      fontSize: 14, fontWeight: 600
    }}>
      {n}
    </Box>
    <Box sx={{flex: 1, minWidth: 0}}>
      <Typography id={ id } sx={{fontSize: 16, fontWeight: 600, lineHeight: '28px', mb: 1}}>{title}</Typography>
      {children}
    </Box>
  </Box>
);

const ExistingDltForm = ({onBack, onDone}) => {
  const {tmId, tmName} = FIRST_RUN_SUPPORT;
  const [peId, setPeId] = useState('');
  const [headersText, setHeadersText] = useState('');
  // A field's error shows once it was left (blur) or Save was clicked, and
  // then follows the value as it is corrected.
  const [touched, setTouched] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);

  const senderIds = parseSenderIds(headersText);
  const found = validateDltForm({peId, senderIds});
  const errors = {
    peId: touched.peId ? found.peId : '',
    senderIds: touched.senderIds ? found.senderIds : ''
  };
  const touch = (field) => () => setTouched((t) => ({...t, [field]: true}));

  const submit = async () => {
    setTouched({peId: true, senderIds: true});
    if (Object.keys(found).length > 0) {
      return;
    }
    setSaving(true);
    setSubmitError('');
    try {
      await submitExistingDlt({peId: normalizePeId(peId), senderIds});
      const after = await onDone();
      if (after && after.showFirstRun) {
        setSubmitError('Your details were saved, but setup is not complete yet. Please check them and try again.');
        return;
      }
      reloadApp();
    } catch (err) {
      setSubmitError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{pt: 3, pb: 0.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
          <Box component="span" sx={{fontSize: 26, fontWeight: 700, lineHeight: 1.25}}>Add your DLT details</Box>
          <Chip
            label="Last step"
            size="small"
            sx={{
              fontSize: 13, fontWeight: 500, color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1)
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{fontSize: 14, mb: 2.5}}>
          Enter your DLT registration details to start sending SMS from your Combirds account.
        </Typography>
        <Stack spacing={ 2.5 }>
          {tmId ?
            <Callout tone="warning">
              Log in to your DLT platform and send a PE-TM chain request from the PE-TM section to our TM ID:
              {' '}{tmId}{tmName ? ` (${tmName})` : ''}.
            </Callout>
            : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          <NumberedStep n={ 1 } id="first-run-pe-id" title="Enter your Principal Entity (PE) ID">
            <TextField
              fullWidth
              size="small"
              placeholder="Principal Entity ID (PE ID)"
              inputProps={{'aria-labelledby': 'first-run-pe-id', inputMode: 'numeric'}}
              value={ peId }
              onChange={ (e) => setPeId(e.target.value) }
              onBlur={ touch('peId') }
              error={ !!errors.peId }
              helperText={ errors.peId || '19 digits, from your DLT portal (e.g. 1201234567890123456).' }
            />
          </NumberedStep>
          <Divider />
          <NumberedStep n={ 2 } id="first-run-headers" title="Enter your approved header (sender ID)">
            <TextField
              fullWidth
              size="small"
              placeholder="Header (sender ID)"
              inputProps={{'aria-labelledby': 'first-run-headers'}}
              value={ headersText }
              onChange={ (e) => setHeadersText(e.target.value.toUpperCase()) }
              onBlur={ touch('senderIds') }
              error={ !!errors.senderIds }
              helperText={ errors.senderIds ||
                `Enter your approved header(s), separated by commas (e.g. ACMEIN, ACMEOT). You can add up to ${MAX_SENDER_IDS} headers.` }
            />
          </NumberedStep>
          <Callout tone="info" sx={{py: 1.75}}>
            <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, alignItems: {md: 'center'}, gap: 2}}>
              <Box sx={{flex: 1, minWidth: 0}}>
                <Box component="strong" sx={{display: 'block', fontSize: 14, fontWeight: 600}}>Need help?</Box>
                If you need help finding your PE ID or approved headers, or with the PE-TM chain request, SMS
                setup, API integration, or recharge, our support team is here to assist you.
              </Box>
              <Divider orientation="vertical" flexItem sx={{display: {xs: 'none', md: 'block'}}} />
              <SupportContacts nowrap sx={{flex: {md: '0 0 auto'}, gap: 2.5}} />
            </Box>
          </Callout>
        </Stack>
      </DialogContent>
      <DialogActions sx={{justifyContent: 'space-between', alignItems: 'flex-start', px: 3, py: 2, borderTop: 1, borderColor: 'divider'}}>
        <Button onClick={ onBack } disabled={ saving } sx={{textTransform: 'none', fontWeight: 600, fontSize: 15}}>
          Back
        </Button>
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5}}>
          <Button
            variant="contained"
            onClick={ submit }
            disabled={ saving }
            endIcon={ saving ? null : <ArrowForwardIcon /> }
            sx={{textTransform: 'none', fontWeight: 600, fontSize: 15}}
          >
            {saving ? 'Saving…' : 'Save and continue'}
          </Button>
          <Typography color="text.secondary" sx={{fontSize: 12}}>
            Save this and you&apos;re all set to start sending SMS.
          </Typography>
        </Box>
      </DialogActions>
    </>
  );
};

export default FirstRun;
